import { diasEntre, hojeDoUsuario } from "./dia.js";
/**
 * Formatação de data para leitura humana — **um lugar só**.
 *
 * O Noesis guarda tudo em ISO (`2026-08-28`), porque é o que ordena, compara e
 * atravessa fuso sem ambiguidade. Mas ISO é formato de máquina: um brasileiro lê
 * `28/08/2026`, e ler `2026-08-27` numa tela e `2026-08-28` em outra é o tipo de
 * detalhe que faz duvidar do resto.
 *
 * **Por que um módulo e não uma chamada de `Intl` em cada tela.** Porque foi
 * exatamente assim que a divergência apareceu: uma tela usava `Intl`, outra
 * imprimia a string crua, uma terceira cortava com `slice(0, 10)`. Com o
 * formatador centralizado, tela nova não tem como inventar um quarto jeito.
 *
 * Todas as funções recebem **dia civil** (`AAAA-MM-DD`) ou **instante ISO UTC**,
 * e nunca `Date`: o `Date` do JavaScript interpreta `2026-08-28` como meia-noite
 * UTC, que em São Paulo é o dia 27 — a armadilha do invariante 16 vestida de
 * formatação.
 */
const MESES_CURTOS = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
const MESES_LONGOS = [
    "janeiro", "fevereiro", "março", "abril", "maio", "junho",
    "julho", "agosto", "setembro", "outubro", "novembro", "dezembro",
];
const DIAS_DA_SEMANA = ["domingo", "segunda-feira", "terça-feira", "quarta-feira", "quinta-feira", "sexta-feira", "sábado"];
/** Parte `AAAA-MM-DD` sem passar por `Date`, que mudaria o dia pelo fuso. */
function partes(dia) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dia);
    if (!m)
        return null;
    return { ano: Number(m[1]), mes: Number(m[2]), data: Number(m[3]) };
}
/** O dia civil de um instante ISO UTC, ou o próprio valor se já for um dia. */
export function comoDia(valor, fuso) {
    if (/^\d{4}-\d{2}-\d{2}$/.test(valor))
        return valor;
    // Instante: converte no fuso do usuário quando ele foi informado. Sem fuso,
    // corta em UTC — o que só é aceitável em contexto que já é UTC.
    if (fuso) {
        const f = new Intl.DateTimeFormat("en-CA", { timeZone: fuso, year: "numeric", month: "2-digit", day: "2-digit" });
        return f.format(new Date(valor));
    }
    return valor.slice(0, 10);
}
/** `28/08/2026` — o formato que se lê sem pensar no Brasil. */
export function dataCurta(dia, fuso) {
    const p = partes(comoDia(dia, fuso));
    if (!p)
        return dia;
    return `${String(p.data).padStart(2, "0")}/${String(p.mes).padStart(2, "0")}/${p.ano}`;
}
/** `28 ago 2026` — para lista, onde a barra vira ruído em coluna. */
export function dataMedia(dia, fuso) {
    const p = partes(comoDia(dia, fuso));
    if (!p)
        return dia;
    return `${p.data} ${MESES_CURTOS[p.mes - 1]} ${p.ano}`;
}
/** `28 de agosto de 2026` — para cabeçalho, onde há espaço. */
export function dataPorExtenso(dia, fuso) {
    const p = partes(comoDia(dia, fuso));
    if (!p)
        return dia;
    return `${p.data} de ${MESES_LONGOS[p.mes - 1]} de ${p.ano}`;
}
/** `setembro de 2026` — o cabeçalho de um mês no seletor de dia. */
export function mesEAnoPorExtenso(dia) {
    const p = partes(dia);
    if (!p)
        return dia;
    return `${MESES_LONGOS[p.mes - 1]} de ${p.ano}`;
}
/**
 * `sexta-feira, 28 de agosto` — a saudação da home.
 *
 * O dia da semana é calculado em UTC ao meio-dia de propósito: o dia civil já
 * chegou resolvido, e usar meio-dia evita que qualquer deslocamento de fuso
 * empurre o cálculo para o dia vizinho.
 */
export function diaDaSemanaPorExtenso(dia) {
    const p = partes(dia);
    if (!p)
        return dia;
    const d = new Date(Date.UTC(p.ano, p.mes - 1, p.data, 12));
    return `${DIAS_DA_SEMANA[d.getUTCDay()]}, ${p.data} de ${MESES_LONGOS[p.mes - 1]}`;
}
/**
 * `hoje`, `ontem`, `há 3 dias`, `em 6 dias` — ou a data quando ficar longe.
 *
 * Relativo só perto: "há 187 dias" obriga a fazer conta, enquanto `23/02/2026`
 * se lê direto. O corte é em duas semanas para cada lado.
 */
export function dataRelativa(dia, hoje, fuso) {
    const alvo = comoDia(dia, fuso);
    const delta = diasEntre(hoje, alvo);
    if (delta === 0)
        return "hoje";
    if (delta === 1)
        return "amanhã";
    if (delta === -1)
        return "ontem";
    if (delta > 1 && delta <= 14)
        return `em ${delta} dias`;
    if (delta < -1 && delta >= -14)
        return `há ${-delta} dias`;
    return dataCurta(alvo);
}
/** `28/08/2026 às 22:41`, no fuso do usuário. Para instante, não para dia. */
export function instantePorExtenso(instanteIso, fuso) {
    const hora = new Intl.DateTimeFormat("pt-BR", {
        timeZone: fuso,
        hour: "2-digit",
        minute: "2-digit",
    }).format(new Date(instanteIso));
    return `${dataCurta(instanteIso, fuso)} às ${hora}`;
}
/** O dia de hoje no fuso do usuário, já formatado. Atalho de conveniência. */
export function hojeCurto(fuso) {
    return dataCurta(hojeDoUsuario(fuso));
}
/**
 * Contagem com o substantivo na forma certa.
 *
 * O defeito que ela conserta é pequeno e constante: a tela de estatísticas
 * escrevia **"1 meses"** na faixa de janelas, e o simulado escrevia
 * "1 modelo(s) de prova". Os dois eram o mesmo atalho — concatenar o número com
 * o plural, ou fugir com um "(s)" que ninguém lê em voz alta. Não existia um
 * lugar para isso: `formato.ts` cuidava de data e mais nada.
 *
 * O plural é opcional porque em português o `-s` cobre a maioria; quando não
 * cobre (`mês` → `meses`), passa-se a forma inteira.
 */
export function plural(quantidade, singular, plural) {
    const palavra = Math.abs(quantidade) === 1 ? singular : (plural ?? `${singular}s`);
    return `${quantidade} ${palavra}`;
}
//# sourceMappingURL=formato.js.map